import React, { useEffect, useState } from 'react';
import { bookingApi } from '../../utils/bookingApi';
import { formatDate } from '../../utils/dates';
import { useConfirm } from '../ui';

// One row per session type that needs approval (DMT, say), for a gymnast on their
// profile: approved (by whom, when) or not, and Approve / Revoke for staff.
// Rendered inside the profile's info list, so each row is an <li>.
export default function SessionTypeApprovals({ gymnast, editable, onUpdated, itemStyle, keyStyle }) {
  const confirm = useConfirm();
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    bookingApi.getSessionTypes()
      .then(r => live && setTypes(r.data.filter(t => t.needsApproval && !t.archived)))
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const approvals = gymnast.sessionTypeApprovals || [];

  const toggle = async (type, approved) => {
    if (approved && !(await confirm({
      title: `Revoke ${type.name} approval?`,
      message: `They won't be able to book ${type.name} sessions until a coach approves them again.`,
      confirmLabel: 'Revoke',
    }))) return;
    setBusy(type.id);
    setError(null);
    try {
      await bookingApi.setSessionTypeApproval(gymnast.id, type.id, !approved);
      onUpdated();
    } catch (err) {
      setError({ id: type.id, message: err.response?.data?.error || `Couldn't update ${type.name} approval.` });
    } finally {
      setBusy(null);
    }
  };

  return types.map(type => {
    const approval = approvals.find(a => a.sessionTypeId === type.id);
    return (
      <li key={type.id} style={{ ...itemStyle, borderBottom: 'none' }}>
        <span style={keyStyle}>{type.name}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          {approval ? (
            <>
              <span className="bk-pill bk-pill--good">Approved</span>
              <span className="bk-small">
                {approval.approvedBy && `by ${approval.approvedBy.firstName} ${approval.approvedBy.lastName} `}
                on {formatDate(approval.approvedAt)}
              </span>
            </>
          ) : (
            <span className="bk-pill bk-pill--off">Not approved</span>
          )}
          {editable && (
            <button
              type="button"
              className={`bk-btn bk-btn--sm${approval ? ' bk-btn--outline-danger' : ''}`}
              disabled={busy === type.id}
              onClick={() => toggle(type, !!approval)}
            >
              {approval ? 'Revoke' : 'Approve'}
            </button>
          )}
          {error?.id === type.id && <span className="bk-error" role="alert">{error.message}</span>}
        </span>
      </li>
    );
  });
}
